import { Button, Col, Form, Input, message, Radio, Row } from "antd";
import { RadioChangeEvent } from "antd/lib/radio";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useParams } from "react-router";
import { ErrorMessages, exceptionHandler } from "shared-components";
import { API } from "shared-components/src/api/api";

import { DoneMessage } from "../../atoms/DoneMessage";
import { ComponentWrapper } from "../Layout/ComponentWrapper";

const useStyles = createUseStyles((theme: any) => {
  return {
    formItem: {
      marginBottom: 24,
      "& label": {
        fontSize: "14px",
        lineHeight: "22px",
        color: theme.gray9,
        fontWeight: "normal",
        marginBottom: 2
      },

      "& .ant-form-item-explain": {
        color: theme.red6,
        marginTop: 2
      },
      "& input": {
        fontSize: "14px"
      }
    },
    fullWidth: {
      maxWidth: "100%",
      borderRadius: 2
    },
    formWrapper: {
      padding: 24,
      background: "white",
      height: "-webkit-fill-available"
    },
    button: {
      borderRadius: 35,
      background: theme.blue6,
      borderColor: theme.blue6
    },
    container: {
      margin: 20
    },
    updateContainer: {
      width: "100%",
      height: 676,
      margin: 20
    }
  };
});

const TeamForm = (props: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id }: any = useParams();
  const [form] = Form.useForm();

  const [state, setState] = useState({
    team_name: "",
    owner_name: "",
    email: "",
    password: "",
    external_team: false
  });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(true);

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const fetchData = async (id: string) => {
    try {
      const response = await API.get(`admin/teams/${id}`);
      if (response && response.data) {
        setState({
          ...state,
          owner_name: response.data.user.name,
          team_name: response.data.team.name,
          email: response.data.user.email,
          external_team: response.data.team.external_team
        });
        form.setFieldsValue({
          owner_name: response.data.user.name,
          team_name: response.data.team.name,
          email: response.data.user.email,
          external_team: response.data.team.external_team
        });
      }
    } catch (error) {
      exceptionHandler(error, t);
    }
  };

  const handleData = async () => {
    setLoading(true);
    try {
      const response = !id
        ? await API.post(`admin/teams`, state)
        : await API.put(`admin/teams/${id}`, state);
      if (response && response.data) {
        setLoading(false);
        setDone(true);
      }
    } catch (error) {
      if (error.code && ErrorMessages[error.code]) {
        message.error(t(ErrorMessages[error.code]));
        return;
      }
      exceptionHandler(error, t);
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | RadioChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    if (name) {
      setState(s => {
        return { ...s, [name]: value };
      });
    }
  };

  if (done) {
    return (
      <ComponentWrapper back title="Edit Team">
        <div className={classes.updateContainer}>
          <div className={classes.formWrapper}>
            <DoneMessage
              message={t(
                id ? "The team has been updated." : "The team has been added."
              )}
              redirectTo="/teams"
            />
          </div>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper back title={id ? "Edit Team" : "Add Team"}>
      <div className={classes.container}>
        <div className={classes.formWrapper}>
          <Row>
            <Col lg={8}>
              <Form
                onFinish={handleData}
                layout="vertical"
                scrollToFirstError
                form={form}
              >
                <Form.Item
                  required={false}
                  name="team_name"
                  label={t("Team name")}
                  className={classes.formItem}
                  rules={[
                    {
                      required: true,
                      message: t("Required")
                    },
                    {
                      max: 60,
                      message: t("Max Length Reached")
                    }
                  ]}
                >
                  <Input
                    name="team_name"
                    data-testid="team_name"
                    className={classes.fullWidth}
                    onChange={handleChange}
                  />
                </Form.Item>

                <Form.Item
                  required={false}
                  name="owner_name"
                  label={t("Owner name")}
                  className={classes.formItem}
                  rules={[
                    {
                      required: true,
                      message: t("Required")
                    },
                    {
                      max: 60,
                      message: t("Max Length Reached")
                    }
                  ]}
                >
                  <Input
                    name="owner_name"
                    data-testid="owner_name"
                    className={classes.fullWidth}
                    onChange={handleChange}
                  />
                </Form.Item>
                <Form.Item
                  id="email"
                  required={false}
                  name="email"
                  label={t("Mail address")}
                  className={classes.formItem}
                  rules={[
                    {
                      required: true,
                      message: t("Required")
                    },
                    {
                      type: "email",
                      message: t("Not a valid email")
                    },
                    {
                      max: 60,
                      message: t("Max Length Reached")
                    }
                  ]}
                >
                  <Input
                    name="email"
                    data-testid="email"
                    className={classes.fullWidth}
                    onChange={handleChange}
                    onFocus={() => {
                      return setReadOnly(false);
                    }}
                    readOnly={readOnly}
                  />
                </Form.Item>
                <Form.Item
                  label={t("Password")}
                  name="password"
                  required={false}
                  className={classes.formItem}
                  rules={[
                    {
                      required: !props.edit,
                      message: t("Required")
                    },
                    {
                      min: 8,
                      message: t("Must be at least 8 characters")
                    }
                  ]}
                >
                  <Input.Password
                    name="password"
                    type="password"
                    className={classes.fullWidth}
                    onChange={handleChange}
                    data-testid="password"
                    onFocus={() => {
                      return setReadOnly(false);
                    }}
                    readOnly={readOnly}
                  />
                </Form.Item>

                <Form.Item
                  id="external_team"
                  required={false}
                  name="external_team"
                  label={t("External Team")}
                  className={classes.formItem}
                  rules={[
                    {
                      required: true,
                      message: t("Required")
                    }
                  ]}
                >
                  <Radio.Group
                    name="external_team"
                    onChange={handleChange}
                    data-testid="external_team"
                  >
                    <Radio value={true}>{t("Yes")}</Radio>
                    <Radio value={false}>{t("No")}</Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    loading={loading}
                    className={classes.button}
                    htmlType="submit"
                    size="middle"
                    data-testid="submit"
                  >
                    {id ? t("Update") : t("Add")}
                  </Button>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </div>
      </div>
    </ComponentWrapper>
  );
};

export { TeamForm };
